import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "@/api/client";
import { CaptchaField, useCaptcha } from "@/components/Captcha";
import { useSession } from "@/state/session";

export function SignIn() {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const captcha = useCaptcha("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Tracked separately from `error` because it is not a failure the person can
  // fix by typing more carefully - the credentials were right. The remedy is a
  // link, so it gets its own notice rather than a red box.
  const [unverified, setUnverified] = useState(false);
  const [busy, setBusy] = useState(false);
  const justReset = Boolean((location.state as { passwordReset?: boolean } | null)?.passwordReset);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setUnverified(false);
    try {
      await signIn(email, password, captcha.fields());
      navigate("/profiles");
    } catch (cause) {
      if (cause instanceof ApiError && cause.isUnverifiedEmailError) setUnverified(true);
      else setError(cause instanceof ApiError ? (cause.fieldMessage ?? cause.message) : "Could not sign in");
    } finally {
      setBusy(false);
      // Every exit path, including success. The server burned this token on
      // the attempt whatever the outcome, so leaving the old image on screen
      // would hand the viewer an unsolvable challenge on their next try.
      void captcha.reload();
    }
  }

  return (
    <main className="page container" style={{ display: "grid", placeItems: "center" }}>
      <form className="panel panel--narrow" onSubmit={submit} style={{ marginTop: 80 }}>
        <h1 style={{ marginBottom: 22 }}>Sign in</h1>
        {justReset && !error && !unverified && (
          <div className="alert alert--info">
            Your password has been changed. Sign in with the new one.
          </div>
        )}
        {error && <div className="alert alert--error">{error}</div>}
        {unverified && (
          <div className="alert alert--info">
            This account is waiting on a confirmation link. Check your inbox, or{" "}
            <Link to="/verify-email" style={{ color: "inherit", textDecoration: "underline" }}>
              send it again
            </Link>
            .
          </div>
        )}
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <span style={{ fontSize: 12 }}>
            <Link to="/forgot-password" style={{ color: "var(--paper-400)" }}>
              Forgotten your password?
            </Link>
          </span>
        </div>
        <CaptchaField state={captcha} />
        <button className="btn btn--accent btn--block" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <p style={{ marginTop: 18, color: "var(--paper-400)", fontSize: 13 }}>
          New here? <Link to="/signup" style={{ color: "var(--accent)" }}>Create an account</Link>
        </p>
      </form>
    </main>
  );
}
